import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Copy,
  Check,
  Users,
  DollarSign,
  Award,
  Clock,
  CheckCircle,
  XCircle,
  Edit,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { PageLoader } from '@/components/ui/Spinner';
import { useReferralUser } from '@/hooks/useReferrals';
import { TIER_CONFIG, STATUS_CONFIG } from '@/types/referral.types';
import AdjustCreditsModal from './AdjustCreditsModal';
import ChangeTierModal from './ChangeTierModal';

const WHATSAPP_NUMBER = '447934514016';

function formatPhone(phone: string | undefined | null) {
  if (!phone) return '-';
  if (phone.startsWith('55') && phone.length >= 12) {
    const country = phone.slice(0, 2);
    const area = phone.slice(2, 4);
    const part1 = phone.slice(4, 9);
    const part2 = phone.slice(9);
    return `+${country} ${area} ${part1}-${part2}`;
  }
  return `+${phone}`;
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatDateTime(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function ReferralUserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const { data, isLoading, isError } = useReferralUser(userId || '');

  const [copied, setCopied] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [showTierModal, setShowTierModal] = useState(false);

  const inviteLink = data?.user?.referralCode
    ? `https://wa.me/${WHATSAPP_NUMBER}?text=${data.user.referralCode}`
    : '';

  const handleCopyLink = async () => {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (isLoading) {
    return <PageLoader message="Carregando usuario..." />;
  }

  if (isError || !data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <p className="text-red-500 dark:text-red-400">
          Erro ao carregar usuario
        </p>
      </div>
    );
  }

  const { user, referrals, rewards } = data;

  // Handle both old and new API response formats
  const userName = user.user?.name || user.name || 'Sem nome';
  const userPhone = user.user?.phoneNumber || user.phoneNumber;
  const userTier = user.currentTier || user.tier || 'bronze';
  const userCredits = user.availableCredits
    ? parseFloat(user.availableCredits)
    : (user.credits || 0);
  const tierConfig = TIER_CONFIG[userTier];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/admin/referrals/users">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {userName}
            </h1>
            <p className="text-gray-500 dark:text-gray-400">
              {formatPhone(userPhone)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => setShowCreditsModal(true)}
            leftIcon={<DollarSign className="w-4 h-4" />}
          >
            Ajustar Creditos
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowTierModal(true)}
            leftIcon={<Edit className="w-4 h-4" />}
          >
            Mudar Tier
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${tierConfig.bgColor}`}>
                <Award className={`w-5 h-5 ${tierConfig.color}`} />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Tier</p>
                <p className={`font-bold ${tierConfig.color}`}>{tierConfig.label}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Indicacoes</p>
                <p className="font-bold text-gray-900 dark:text-white">
                  {user.validatedReferrals ?? 0}
                  {(user.pendingReferrals ?? 0) > 0 && (
                    <span className="text-sm text-yellow-600 ml-1">
                      (+{user.pendingReferrals})
                    </span>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Creditos</p>
                <p className="font-bold text-gray-900 dark:text-white">
                  R$ {userCredits.toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                <Clock className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Membro desde</p>
                <p className="font-bold text-gray-900 dark:text-white">
                  {formatDate(user.createdAt)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Benefits based on tier */}
      <Card>
        <CardHeader>
          <CardTitle>Beneficios do Tier {tierConfig.label}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {tierConfig.benefits.map((benefit, idx) => (
              <Badge key={idx} variant="secondary">{benefit}</Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Invite Link */}
      <Card>
        <CardHeader>
          <CardTitle>Link de Convite</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="flex-1 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg font-mono text-sm break-all">
              <p className="text-gray-500 dark:text-gray-400 text-xs mb-1">Codigo:</p>
              <p className="font-bold text-gray-900 dark:text-white">{user.referralCode}</p>
            </div>
            <Button
              onClick={handleCopyLink}
              leftIcon={copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            >
              {copied ? 'Copiado!' : 'Copiar Link'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Referrals history */}
      <Card>
        <CardHeader>
          <CardTitle>Historico de Indicacoes ({referrals.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {referrals.length === 0 ? (
            <p className="text-center text-gray-500 dark:text-gray-400 py-8">
              Nenhuma indicacao realizada ainda
            </p>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {referrals.map((referral) => {
                const statusConfig = STATUS_CONFIG[referral.status];
                return (
                  <div key={referral.id} className="py-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {referral.refereeName || 'Sem nome'}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {formatPhone(referral.refereePhone || '')} - {formatDateTime(referral.createdAt)}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      {referral.premiumDaysAwarded && (
                        <span className="text-sm text-green-600 dark:text-green-400">
                          +{referral.premiumDaysAwarded} dias
                        </span>
                      )}
                      {referral.creditsAwarded && (
                        <span className="text-sm text-blue-600 dark:text-blue-400">
                          R$ {referral.creditsAwarded}
                        </span>
                      )}
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.bgColor} ${statusConfig.color}`}>
                        {referral.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                        {referral.status === 'validated' && <CheckCircle className="w-3 h-3 mr-1" />}
                        {referral.status === 'rejected' && <XCircle className="w-3 h-3 mr-1" />}
                        {statusConfig.label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rewards history */}
      <Card>
        <CardHeader>
          <CardTitle>Historico de Rewards ({rewards.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {rewards.length === 0 ? (
            <p className="text-center text-gray-500 dark:text-gray-400 py-8">
              Nenhum reward recebido ainda
            </p>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {rewards.map((reward) => (
                <div key={reward.id} className="py-3 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {reward.type === 'premium_days' && `+${reward.premiumDays} dias premium`}
                      {reward.type === 'credits' && `R$ ${reward.creditAmount} em creditos`}
                      {reward.type === 'both' && `+${reward.premiumDays} dias + R$ ${reward.creditAmount}`}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {formatDateTime(reward.createdAt)}
                    </p>
                  </div>
                  <Badge
                    variant={
                      reward.status === 'applied' ? 'success' :
                      reward.status === 'pending' ? 'warning' : 'danger'
                    }
                  >
                    {reward.status === 'applied' ? 'Aplicado' :
                     reward.status === 'pending' ? 'Pendente' : 'Expirado'}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <AdjustCreditsModal
        isOpen={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
        userId={user.userId || user.id}
        currentCredits={userCredits}
      />

      <ChangeTierModal
        isOpen={showTierModal}
        onClose={() => setShowTierModal(false)}
        userId={user.userId || user.id}
        currentTier={userTier}
      />
    </div>
  );
}
